import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { isIosBrowser } from '../../lib/device';
import { APP_STORE_URL, openExternal } from '../../lib/appStore';
import { useReducedMotion, SPRING_POP, EASE_OUT_IOS } from '../../lib/motion';
import brandIcon from '../../assets/clash-icon.svg';

const DISMISS_KEY = 'h2h:getAppBannerDismissed';

/**
 * App Store smart-banner for iOS visitors using a *browser* (Safari/Chrome) rather
 * than the installed native app — shown across the whole app (landing, sign-up,
 * log-in and once they're inside) so any iPhone/iPad user is nudged toward the real
 * native experience. Never renders in the native app, on Android/desktop, or as an
 * installed PWA. Dismissable; the choice sticks via localStorage.
 */
export function GetTheAppBanner() {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIosBrowser()) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      dismissed = false;
    }
    if (!dismissed) setShow(true);
  }, []);

  function dismiss() {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — fine, it'll just show again next visit */
    }
  }

  function openStore() {
    openExternal(APP_STORE_URL);
    dismiss();
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: -100 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -100 }}
          transition={reduce ? { duration: 0.2, ease: EASE_OUT_IOS } : SPRING_POP}
          className="fixed inset-x-0 top-0 z-[var(--z-toast)] px-3 pt-[calc(env(safe-area-inset-top)+8px)]"
        >
          <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-night/95 px-3 py-2.5 text-white shadow-[0_18px_40px_-14px_rgba(0,0,0,0.7)] backdrop-blur-md">
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[15px] font-bold text-white/55 transition active:scale-90"
            >
              ×
            </button>
            <img
              src={brandIcon}
              alt=""
              className="h-10 w-10 shrink-0 rounded-[10px] shadow-[0_4px_10px_-2px_rgba(0,0,0,0.5)]"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-extrabold leading-tight">HeadToHead</div>
              <div className="truncate text-[12px] font-medium text-white/60">
                Faster, smoother in the iPhone app
              </div>
            </div>
            <button
              type="button"
              onClick={openStore}
              className="shrink-0 rounded-full bg-accent px-4 py-1.5 text-[13px] font-extrabold text-night transition active:scale-95"
            >
              Get
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
